'use client';

import type { Household } from 'src/types/household';
import type { BaseClient } from 'src/types/shared';
import { useQuery } from '@apollo/client';
import { graphql } from 'src/gql';

const GET_HOUSEHOLD_SEARCH = graphql(/* GraphQL */ `
  query HouseholdSearch($query: String!) {
    households(query: $query, first: 10) {
      totalCount
      edges {
        matchInfos {
          field
          value
          context
        }
        node {
          id
          accountCount
          advisorAnnualRevenue
          householdStage
          lastContactAt
          name
          orionHouseholdId
          salesforceAccountId
          uuid
          savvyAum
          organizationHouseholdTags {
            id
            name
          }
          primaryClient {
            id
            employerName
            user {
              id
              firstName
              lastName
              email
              phoneNumber
            }
          }
          coClient {
            id
            employerName
            user {
              id
              firstName
              lastName
              email
              phoneNumber
            }
          }
          advisor {
            user {
              fullName
            }
          }
          prospectData {
            estimatedAum
          }
        }
      }
    }
  }
`);

export type HouseholdMatchField =
  | 'HOUSEHOLD_NAME'
  | 'CLIENT_NAME'
  | 'EMAIL'
  | 'PHONE'
  | 'SECONDARY_EMAIL'
  | 'SECONDARY_PHONE';

export type HouseholdMatchInfo = {
  field: HouseholdMatchField;
  value: string;
  context: string | null;
};

export type HouseholdSearchResponse = {
  households: {
    totalCount: number;
    edges: {
      matchInfos: HouseholdMatchInfo[];
      node: Omit<Household, 'primaryClient' | 'coClient'> & {
        accountCount: number;
        organizationHouseholdTags: { id: string; name: string }[];
        advisor: {
          user: {
            fullName: string;
          };
        } | null;
        primaryClient: (BaseClient & { employerName: string | null }) | null;
        coClient: (BaseClient & { employerName: string | null }) | null;
      };
    }[];
  };
};

type UseHouseholdSearchParams = {
  query: string;
  skip?: boolean;
};

export function useHouseholdSearch({ query, skip = false }: UseHouseholdSearchParams) {
  return useQuery<HouseholdSearchResponse>(GET_HOUSEHOLD_SEARCH, {
    variables: { query },
    skip: skip || !query,
    fetchPolicy: 'cache-and-network',
  });
}
